import React, { useEffect, useState, useRef } from "react";
import { TableFooter } from '../../../Utils/helper';
import { getApi } from '../../../Services/apiService';
import axios from 'axios';

// Column definitions for Negative Estimate Stock Table
export const NEGATIVE_ESTIMATE_STOCK_COLUMNS = [
  { key: 'date', label: 'Delivery date', required: true },
  { key: 'product_code', label: 'Product Code', required: true },
  { key: 'product_name_tc', label: 'Product Name', required: true },
  { key: 'specification', label: 'Specification', required: false },
  { key: 'category', label: 'Category', required: false },
  { key: 'coo', label: 'Origin', required: false },
  { key: 'total_qty', label: 'Total Qty', required: true },
  { key: 'estimated_stock', label: 'Estimate stock', required: true },
];

const NegativeEstimateStockTable = ({ 
  days, 
  visibleColumns, 
  onColumnVisibilityChange,
  dateRange,
  selectedCategoryIds,
  selectedOriginIds,
  isVisible = true,
  onToggleVisibility
}) => {
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const filterRef = useRef(null);
  const [tableData, setTableData] = useState([]);
  const [allTableData, setAllTableData] = useState([]); // Store all data for frontend pagination
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const abortControllerRef = useRef(null);

  // API call to fetch data
  useEffect(() => {
    // If table is hidden, don't fetch data to save resources
    if (!isVisible) return;

    // Cancel previous request if it exists
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create new abort controller for this request
    abortControllerRef.current = new AbortController();

    const fetchData = async () => {
      let isCancelled = false;
      try {
        setLoading(true);
        setError(null);

        const queryParams = new URLSearchParams();

        // Category filter
        if (Array.isArray(selectedCategoryIds) && selectedCategoryIds.length > 0) {
          selectedCategoryIds.forEach(id => queryParams.append('category_ids[]', id));
        }

        // Origin filter
        if (Array.isArray(selectedOriginIds) && selectedOriginIds.length > 0) {
          selectedOriginIds.forEach(id => queryParams.append('origin_ids[]', id));
        }

        // Date range filter (required for this endpoint)
        if (dateRange.startDate && dateRange.endDate) {
          queryParams.append('from_date', dateRange.startDate);
          queryParams.append('to_date', dateRange.endDate);
        } else {
          // If no date range, don't fetch
          setTableData([]);
          setLoading(false);
          return;
        }

        // Required parameters for negative-estimate-stock endpoint
        // Note: This endpoint doesn't support pagination, so we fetch all data and paginate on frontend
        queryParams.append('search', ''); // Empty search for now

        const response = await getApi(`/dashboard/negative-estimate-stock?${queryParams.toString()}`, {
          signal: abortControllerRef.current.signal
        });

        if (response.success) {
          // Response is a direct array (not paginated)
          const inventories = response.data.inventories || [];
          setAllTableData(inventories);
        } else {
          setError(response?.msg || 'Failed to fetch negative estimate stock data');
          setAllTableData([]);
        }
      } catch (error) {
        // Don't set error for cancelled requests
        if (axios.isCancel?.(error) || error.name === 'AbortError' || error.name === 'CanceledError') {
          isCancelled = true;
          return;
        }
        setError(error.message || 'Failed to fetch negative estimate stock data');
        setAllTableData([]);
      } finally {
        // Only set loading to false if request wasn't cancelled
        if (!isCancelled) {
          setLoading(false);
        }
      }
    };

    fetchData();

    // Cleanup function to cancel request when component unmounts or dependencies change
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [dateRange, selectedCategoryIds, selectedOriginIds, isVisible]);

  // Cleanup effect to cancel any pending requests when component unmounts
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  // Reset page to 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [dateRange, selectedCategoryIds, selectedOriginIds]);

  // Frontend pagination
  useEffect(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const paginatedData = allTableData.slice(startIndex, endIndex);
    setTableData(paginatedData);
  }, [allTableData, currentPage, itemsPerPage]);

  const pageCount = Math.ceil(allTableData.length / itemsPerPage);
  const handlePageClick = (selectedPage) => {
    setCurrentPage(selectedPage);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (filterRef.current && !filterRef.current.contains(event.target)) {
        setIsFilterOpen(false);
      }
    };

    if (isFilterOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isFilterOpen]);

  const handleColumnToggle = (columnKey) => {
    const column = NEGATIVE_ESTIMATE_STOCK_COLUMNS.find(col => col.key === columnKey);
    if (column && !column.required) {
      onColumnVisibilityChange(columnKey);
    }
  };

  const visibleColumnsList = NEGATIVE_ESTIMATE_STOCK_COLUMNS.filter(col => visibleColumns[col.key] !== false);

  return (
    <div className="dashboard-table-container" style={{ marginBottom: '15px' }}>
      <div className="table-top" style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        marginBottom: '5px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <h3 style={{ marginBottom: 0 }}>Coming {days} days [Direct in/out] & [Prepack in/out] estimate stock = negative</h3>
          <button
            onClick={onToggleVisibility}
            style={{
              background: '#F2F7F6',
              color: '#006E47',
              border: 'none',
              padding: '2px 8px',
              borderRadius: '4px',
              fontSize: '18px',
              fontWeight: 600,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minWidth: '45px'
            }}
            title={isVisible ? "Hide Table" : "Show Table"}
          >
            {isVisible ? '−' : '+'}
          </button>
        </div>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          <div className="dropdown" ref={filterRef} style={{ position: 'relative' }}>
            <button
              className="dropbtn"
              onClick={() => setIsFilterOpen(!isFilterOpen)}
              style={{
                background: '#F2F7F6',
                color: '#006E47',
                border: 'none',
                width: '35px',
                height: '35px',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                borderRadius: '5px',
                fontSize: '16px',
                fontWeight: 600,
                cursor: 'pointer'
              }}
            >
              <img src={window.getPublicUrl("/assets/images/setting.svg")} alt="icon"/>
            </button>
            {isFilterOpen && (
              <div
                className="dropdown-content"
                style={{
                  display: 'block',
                  position: 'absolute',
                  right: 0,
                  top: '40px',
                  background: 'white',
                  boxShadow: '0px 0px 16px 0px #0000000F',
                  borderRadius: '8px',
                  padding: '15px',
                  minWidth: '200px',
                  zIndex: 1000
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <div style={{ marginBottom: '10px', fontWeight: 600, fontSize: '14px' }}>
                  Column Visibility
                </div>
                {NEGATIVE_ESTIMATE_STOCK_COLUMNS.map((column) => (
                  <label
                    key={column.key}
                    className="checbox-cs"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      marginBottom: '8px',
                      cursor: column.required ? 'not-allowed' : 'pointer',
                      opacity: column.required ? 0.6 : 1
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={visibleColumns[column.key] !== false}
                      onChange={() => handleColumnToggle(column.key)}
                      disabled={column.required}
                      style={{ cursor: column.required ? 'not-allowed' : 'pointer' }}
                    />
                    <span className="checkmark"></span>
                    <span style={{ fontSize: '14px' }}>
                      {column.label}
                      {column.required && <span style={{ color: 'red', display:'inline' }}> *</span>}
                    </span>
                  </label>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {isVisible && (
        <>
          <div className="table-outer">
            <table className="table custom-table">
              <thead>
                <tr className="t-heading">
                  {visibleColumnsList.map((column) => (
                    <th key={column.key} style={{ padding: '10px' }}>
                      <h5>
                        {column.label}
                        {column.required && <span style={{ color: 'red', display:'inline' }}> *</span>}
                      </h5>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={visibleColumnsList.length} style={{ textAlign: 'center', padding: '20px' }}>
                      Loading...
                    </td>
                  </tr>
                ) : error ? (
                  <tr>
                    <td colSpan={visibleColumnsList.length} style={{ textAlign: 'center', padding: '20px', color: '#ff4d4f' }}>
                      {error}
                    </td>
                  </tr>
                ) : tableData.length > 0 ? (
                  tableData.map((row) => (
                    <tr key={row.id}>
                      {visibleColumnsList.map((column) => (
                        <td key={column.key} style={{ padding: '10px' }}>
                          {row[column.key]}
                        </td>
                      ))}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={visibleColumnsList.length} style={{ textAlign: 'center', padding: '20px' }}>
                      No data available
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {tableData.length > 0 && (
            <div className="table-footer d-flex justify-content-between table-pagination">
              <select 
                name="" 
                id="" 
                className="affter-change-cs page" 
                value={itemsPerPage} 
                onChange={(e) => { 
                  setItemsPerPage(Number(e.target.value)); 
                  setCurrentPage(1);
                }}
              >
                <option value="10">10</option>
                <option value="20">20</option>
                <option value="30">30</option>
                <option value="40">40</option>
              </select>
              <div>
                <TableFooter
                  pageCount={pageCount || 1}
                  handlePageClick={handlePageClick}
                  currentPage={currentPage}
                />
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default NegativeEstimateStockTable;

